'use client';

import { useEffect } from 'react';
import { scrollBehavior } from '@/lib/scroll-behavior';

/**
 * useAnchorExpansion - Auto-expand collapsed components when navigating to anchor links
 *
 * When the URL hash or an in-page link targets an element inside a collapsed
 * component, this hook expands that component (and any collapsed component
 * around it). A collapsed component is any ancestor whose disclosure button
 * points at it with `aria-controls` and says `aria-expanded="false"`, which
 * covers:
 * - CollapsibleSection (and Footnotes, which is one)
 * - RiskAccordion
 *
 * The target must already be in the DOM, so those components keep their
 * collapsed body rendered (hidden) rather than unmounting it.
 *
 * Usage:
 * ```tsx
 * export default function BlogPost() {
 *   useAnchorExpansion();
 *   return <BlogContent />;
 * }
 * ```
 */
export function useAnchorExpansion() {
  useEffect(() => {
    const handleHashChange = () => {
      expandTargetIfCollapsed();
    };

    // Capture phase: runs before link handlers (TOC) that preventDefault and
    // scroll themselves without ever changing location.hash.
    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest<HTMLAnchorElement>('a[href^="#"]');
      const id = idFromFragment(link?.getAttribute('href'));
      const target = id ? document.getElementById(id) : null;
      if (target) expandCollapsedAncestors(target);
    };

    // Check on initial load (if hash is present)
    if (globalThis.window.location.hash) {
      expandTargetIfCollapsed();
    }

    // Listen for hash changes
    globalThis.window.addEventListener('hashchange', handleHashChange);
    document.addEventListener('click', handleClick, true);
    return () => {
      globalThis.window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleClick, true);
    };
  }, []);
}

/** Decode the target id from a `#fragment` (hash or href) */
function idFromFragment(fragment: string | null | undefined): string | null {
  if (!fragment?.startsWith('#') || fragment.length < 2) return null;
  try {
    return decodeURIComponent(fragment.slice(1));
  } catch {
    return fragment.slice(1);
  }
}

/**
 * Find and expand the component containing the target element
 */
function expandTargetIfCollapsed() {
  const elementId = idFromFragment(globalThis.window.location.hash);
  if (!elementId) return;

  // Wait a bit for DOM to be ready, then try to find and expand
  setTimeout(() => {
    const targetElement = document.getElementById(elementId);
    if (!targetElement) return;

    // Check if target is inside a collapsed component
    expandCollapsedAncestors(targetElement);

    // Scroll to target (browser does this by default, but in case it didn't)
    targetElement.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  }, 100);
}

function clickIfCollapsed(ariaControlsValue: string) {
  const value = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(ariaControlsValue) : ariaControlsValue;
  const button = document.querySelector<HTMLButtonElement>(`button[aria-controls="${value}"]`);

  if (button?.getAttribute('aria-expanded') === 'false') {
    button.click();
  }
}

/**
 * Expand every collapsed component between the element and the document
 * root, innermost first, so a target nested in two collapsed wrappers opens.
 */
function expandCollapsedAncestors(element: Element) {
  let current: Element | null = element;

  while (current && current !== document.documentElement) {
    if (current.id) clickIfCollapsed(current.id);
    current = current.parentElement;
  }
}
